/**
 * Is real GPU acceleration available for the WebGL hero?
 *
 * Headless Chromium (Lighthouse, CI) and machines without a usable GPU fall back
 * to a software rasteriser (SwiftShader / llvmpipe / Mesa / Microsoft's basic
 * renderer). Software-rendering this scene costs ~100ms per frame instead of
 * ~1ms, so a continuous WebGL hero pins the main thread there. On those clients
 * we keep the prerendered static hero instead: cheaper for them, and it lets the
 * three.js chunk stay unloaded.
 *
 * The check is a throwaway context probed once; the result is cached. When the
 * renderer string is masked (some privacy modes return an empty/unknown string)
 * we assume a GPU is present, so a capable-but-opaque client is never downgraded.
 */
let cached: boolean | undefined;

const SOFTWARE = /swiftshader|llvmpipe|software|basic render|microsoft|mesa offscreen/i;

export function hasHardwareGpu(): boolean {
  if (cached !== undefined) return cached;
  if (typeof document === "undefined") return (cached = false);

  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!gl) return (cached = false);

    // Modern Firefox and Chrome expose the real renderer straight from
    // `RENDERER`; only fall back to the debug extension (deprecated in Firefox,
    // which is what emits the console warning) when that string is empty or the
    // legacy generic mask ("WebKit WebGL").
    let renderer = String(gl.getParameter(gl.RENDERER) || "");
    if (!renderer || /^(webkit\s+)?webgl$/i.test(renderer)) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) renderer = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "");
    }

    // Unknown (masked) string → assume hardware; only a known software string
    // stands the client down. The probe context is dropped by GC; we no longer
    // force-lose it, which used to print a spurious "WebGL context was lost".
    return (cached = !SOFTWARE.test(renderer));
  } catch {
    return (cached = false);
  }
}
